import { useEffect, useMemo, useRef, useState } from 'react';
import { Question, type QuestionData } from '~/design-system/components/PlanningChat/ui/Question';
import { Button } from '~/design-system/elements/Button/Button';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { ThinkingIndicatorUI } from '~/design-system/components/PlanningChat/ui/ThinkingIndicatorUI';
import questionStyles from '~/design-system/components/PlanningChat/ui/Question.module.css';
import { FeatureDemoScaleFit } from './FeatureDemoScaleFit';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import styles from './FeatureAgentDemo.module.css';

const AGENT_DEMO_DESIGN_WIDTH = 400;
const AGENT_DEMO_DESIGN_HEIGHT = 460;

const noop = () => {};

type Slot =
  | {
      kind: 'question-single';
      id: string;
      data: QuestionData;
      preselectedOptionIds: string[];
    }
  | {
      kind: 'question-multiple';
      id: string;
      data: QuestionData;
      preselectedOptionIds: string[];
    }
  | {
      kind: 'question-open';
      id: string;
      question: string;
      fullAnswer: string;
    }
  | {
      kind: 'thinking';
      id: string;
      text: string;
    };

const SLOTS: Slot[] = [
  {
    kind: 'question-single',
    id: 'pitch-audience',
    data: {
      id: 'q-pitch-audience',
      question: 'Who are you pitching to?',
      type: 'single',
      options: [
        { id: 'opt-0', label: 'Investors' },
        { id: 'opt-1', label: 'B2B Customers' },
        { id: 'opt-2', label: 'Strategic Partners' },
        { id: 'opt-3', label: 'Internal Leadership' },
      ],
    },
    preselectedOptionIds: ['opt-0'],
  },
  { kind: 'thinking', id: 'think-1', text: 'Got it' },
  {
    kind: 'question-multiple',
    id: 'board-sections',
    data: {
      id: 'q-board-sections',
      question: 'Which sections should we cover?',
      type: 'multiple',
      options: [
        { id: 'opt-0', label: 'Progress' },
        { id: 'opt-1', label: 'KPIs' },
        { id: 'opt-2', label: 'Risks' },
        { id: 'opt-3', label: 'Hiring' },
        { id: 'opt-4', label: 'Cash' },
        { id: 'opt-5', label: 'The ask' },
      ],
    },
    preselectedOptionIds: ['opt-0', 'opt-1', 'opt-2'],
  },
  { kind: 'thinking', id: 'think-2', text: 'On it' },
  {
    kind: 'question-open',
    id: 'round-size',
    question: "What's the round size?",
    fullAnswer: '€500K to €3M',
  },
  { kind: 'thinking', id: 'think-3', text: 'Locked in' },
];

const SLOT_DURATION_MS: Record<Slot['kind'], number> = {
  'question-single': 1800,
  'question-multiple': 2000,
  'question-open': 2300,
  thinking: 1600,
};

export const FeatureAgentDemo = () => {
  const reducedMotion = usePrefersReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (reducedMotion) return;
    const el = wrapRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStarted(true);
          observer.unobserve(el);
        }
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (!started || reducedMotion) return;
    const current = SLOTS[activeIndex];
    const dur = SLOT_DURATION_MS[current.kind];
    const t = window.setTimeout(() => {
      setActiveIndex((i) => (i + 1) % SLOTS.length);
    }, dur);
    return () => window.clearTimeout(t);
  }, [started, reducedMotion, activeIndex]);

  const visibleIndex = reducedMotion ? 0 : started ? activeIndex : -1;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <FeatureDemoScaleFit
        designWidth={AGENT_DEMO_DESIGN_WIDTH}
        designHeight={AGENT_DEMO_DESIGN_HEIGHT}
        scaleMode="transform"
      >
        <div className={styles.deck}>
          {SLOTS.map((slot, i) => (
            <SlotView key={slot.id} slot={slot} active={i === visibleIndex} />
          ))}
        </div>
      </FeatureDemoScaleFit>
    </div>
  );
};

FeatureAgentDemo.displayName = 'FeatureAgentDemo';

interface SlotViewProps {
  slot: Slot;
  active: boolean;
}

const SlotView = ({ slot, active }: SlotViewProps) => {
  return (
    <div
      className={`${styles.slot} ${active ? styles.slotActive : ''}`}
      aria-hidden={!active}
    >
      {slot.kind === 'thinking' ? (
        <ThinkingCard text={slot.text} />
      ) : slot.kind === 'question-open' ? (
        <OpenTypingCard
          question={slot.question}
          fullAnswer={slot.fullAnswer}
          active={active}
        />
      ) : (
        <PreselectedQuestion
          data={slot.data}
          preselectedOptionIds={slot.preselectedOptionIds}
        />
      )}
    </div>
  );
};

interface PreselectedQuestionProps {
  data: QuestionData;
  preselectedOptionIds: string[];
}

const PreselectedQuestion = ({ data, preselectedOptionIds }: PreselectedQuestionProps) => {
  const initialSelectedOptions = useMemo(
    () => new Map<string, string[]>([[data.id, preselectedOptionIds]]),
    [data.id, preselectedOptionIds],
  );

  return (
    <Question
      questions={[data]}
      onClose={noop}
      onSkip={noop}
      onSubmit={noop}
      initialSelectedOptions={initialSelectedOptions}
    />
  );
};

interface OpenTypingCardProps {
  question: string;
  fullAnswer: string;
  active: boolean;
}

const TYPING_START_DELAY_MS = 200;
const TYPING_TOTAL_MS = 850;

const OpenTypingCard = ({ question, fullAnswer, active }: OpenTypingCardProps) => {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!active) return;
    setTyped('');
    const totalChars = fullAnswer.length;
    if (totalChars === 0) return;
    const tickMs = Math.max(30, Math.floor(TYPING_TOTAL_MS / totalChars));
    let i = 0;
    let tickTimer: number | undefined;
    const startTimer = window.setTimeout(() => {
      const advance = () => {
        i += 1;
        setTyped(fullAnswer.slice(0, i));
        if (i < totalChars) {
          tickTimer = window.setTimeout(advance, tickMs);
        }
      };
      advance();
    }, TYPING_START_DELAY_MS);
    return () => {
      window.clearTimeout(startTimer);
      if (tickTimer) window.clearTimeout(tickTimer);
    };
  }, [active, fullAnswer]);

  const hasText = typed.length > 0;

  return (
    <div className={questionStyles.question}>
      <div className={questionStyles.header}>
        <p className={questionStyles.questionText}>{question}</p>
        <div className={questionStyles.headerActions}>
          <Button variant="ghostMuted" size="icon-2xs" onClick={noop} leadIcon="close" />
        </div>
      </div>

      <div className={questionStyles.content}>
        <div className={questionStyles.openInputContainer}>
          <div className={`${questionStyles.optionIcon} ${questionStyles.optionIconBordered}`}>
            <DeckIcon name="edit" size={16} />
          </div>
          <textarea
            className={questionStyles.openInput}
            value={typed}
            placeholder="Type your answer..."
            readOnly
            rows={1}
          />
        </div>
      </div>

      <div className={questionStyles.actions}>
        <div className={questionStyles.actionsLeft} />
        <div className={questionStyles.actionsRight}>
          <Button variant="ghostMuted" size="xs" onClick={noop}>
            Skip
          </Button>
          <Button
            variant="default"
            size="xs"
            onClick={noop}
            tailIcon="arrow-up"
            disabled={!hasText}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ThinkingCardProps {
  text: string;
}

const ThinkingCard = ({ text }: ThinkingCardProps) => (
  <div className={styles.thinkingCard}>
    <ThinkingIndicatorUI text={text} duration={2} />
  </div>
);
